/**
 * El modelo con la versión pegada, para el título de la ficha (ticket #87).
 *
 * La versión (`version_name`) se guardaba pero la ficha no la mostraba: un A1
 * cargado como "35 TFSI" salía como "Audi A1" a secas.
 *
 * Pegarla sin más repite texto, porque muchas automotoras escriben la versión
 * DENTRO del nombre del modelo, o repiten el modelo al principio de la versión.
 * Casos reales del stock (24-sep, 1.965 autos con versión):
 *   - "T-CROSS COMFORTLINE 1.6L" + "Comfortline"  → la versión ya está: no se agrega
 *   - "T60" + "T60 DX 4X4"                        → "T60 DX 4X4", no "T60 T60 DX 4X4"
 *   - "L200 Katana" + "L200 2.4 KATANA CR"        → "L200 Katana 2.4 CR"
 *   - "A1" + "35 TFSI"                            → "A1 35 TFSI"
 *
 * Se compara por palabras enteras y sin mayúsculas, tildes, espacios ni signos,
 * así "35TFSI" y "35 TFSI" cuentan como lo mismo, pero "2.0" no se confunde con
 * el "208" de un Peugeot.
 *
 * Solo arma texto para mostrar: no toca el vehículo ni lo que se manda a las
 * integraciones (ChileAutos, MercadoLibre, Facebook), que arman su propio título.
 *
 * Uso: npm run test:modelo-con-version
 */

const palabras = (valor: unknown): string[] =>
  String(valor ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

/** Las palabras pegadas, sin tildes, mayúsculas ni signos: "35 TFSI" → "35tfsi". */
const compacto = (ps: string[]): string =>
  ps
    .join('')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9+]/g, '');

export const modeloConVersion = (
  modelo: unknown,
  version: unknown,
  marca?: unknown
): string => {
  const mod = palabras(modelo);
  const ver = palabras(version);
  const verCompacta = compacto(ver);
  if (!verCompacta) return mod.join(' ');

  // Se compara contra marca + modelo: hay quien escribe "Audi A1 35 TFSI" en la versión.
  const titulo = [...palabras(marca), ...mod];

  // 1. La versión entera ya aparece en el título, como palabras seguidas.
  for (let i = 0; i < titulo.length; i++) {
    for (let j = i + 1; j <= titulo.length; j++) {
      if (compacto(titulo.slice(i, j)) === verCompacta) return mod.join(' ');
    }
  }
  //    …o salteada: "TIGGO 7 GLX CVT PRO 1.5 AUT" ya dice todo lo de "Tiggo 7 Pro".
  const enTitulo = new Set(titulo.map((p) => compacto([p])));
  const deLaVersion = ver.map((p) => compacto([p])).filter(Boolean);
  if (deLaVersion.every((c) => enTitulo.has(c))) return mod.join(' ');

  // 2. La versión arranca repitiendo el final del título: se descarta ese
  //    principio. Se prueba primero el solape más largo.
  let resto = ver;
  solape: for (let k = ver.length - 1; k >= 1; k--) {
    const inicioVersion = compacto(ver.slice(0, k));
    if (!inicioVersion) continue;
    for (let a = 1; a <= titulo.length; a++) {
      if (compacto(titulo.slice(titulo.length - a)) === inicioVersion) {
        resto = ver.slice(k);
        break solape;
      }
    }
  }

  // 3. De lo que queda, fuera las palabras que ya están en el título (o antes en
  //    la misma versión): "LAND CRUISER PRADO" + "Land Cruiser 2.8 PRADO VX"
  //    → "LAND CRUISER PRADO 2.8 VX". Las de un solo carácter se dejan siempre
  //    (el "3" de "320i M SPORT 3 PUERTAS" no es el de "SERIE 3") y los signos
  //    sueltos ("-", "/") se sacan para que no queden colgando.
  const nuevas = resto.filter((p) => {
    const c = compacto([p]);
    if (c.length === 0) return false;
    if (c.length === 1) return true;
    if (enTitulo.has(c)) return false;
    enTitulo.add(c);
    return true;
  });

  return [...mod, ...nuevas].join(' ');
};
